import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  type MailTask,
  type NameCount,
  type TaskListParams,
  type TaskPref,
  type TaskSummary,
} from "@/api/client";
import Toasts from "@/components/Toasts";
import SessionGuard from "@/components/SessionGuard";
import { toast } from "@/lib/toast";
import {
  ArrowLeft,
  ListChecks,
  Loader2,
  Info,
  RefreshCw,
  Check,
  Clock,
  ExternalLink,
  Paperclip,
  Reply,
  AlertTriangle,
  BellRing,
  BellOff,
  Pencil,
  X,
  Undo2,
} from "lucide-react";

// 메일 업무비서 — 내 메일에서 추출한 할 일·기한·요청 유형 대시보드 (본인 전용).
// 설계: 구축방법/ISAC_메일업무비서_구현계획_v0.2.md §6 / API 는 포털 BFF /api/chat/mail/tasks/*.
//
// ⚠ owner 는 서버가 세션에서 주입한다. 이 화면은 남의 업무를 조회할 방법이 없다.
//   관리자 화면과 달리 여기서만 개인별 업무명·건수가 보인다(운영포털 미노출 결정).

const CARD = "rounded-xl border border-border bg-card p-3";
const LABEL = "mb-1.5 block text-xs font-semibold text-foreground";
const HELP = "mt-1 text-[11px] leading-relaxed text-muted-foreground";
const INPUT =
  "rounded-lg border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary";
const BTN =
  "inline-flex items-center justify-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40";
const BTN_SM = "inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-40";
const CHIP = "rounded-full border px-2 py-0.5 text-[11px]";

type StatusTab = "open" | "done" | "dismissed";

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  return s.length >= 16 ? s.slice(0, 16) : s;
}

function addDays(base: string, n: number): string {
  const d = new Date(base + "T00:00:00");
  d.setDate(d.getDate() + n);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 기한 배지 — 남은 일수에 따라 색을 바꾼다. */
function DueChip({ t }: { t: MailTask }) {
  if (!t.due_date)
    return <span className={CHIP + " border-border bg-muted text-muted-foreground"}>기한 없음</span>;
  const d = t.days_left;
  let cls = "border-border bg-muted text-muted-foreground";
  let label = fmtDate(t.due_date);
  if (d !== null) {
    if (d < 0) {
      cls = "border-red-300 bg-red-50 text-red-700";
      label = `${label} · ${-d}일 지남`;
    } else if (d === 0) {
      cls = "border-red-300 bg-red-50 text-red-700";
      label = `${label} · 오늘`;
    } else if (d <= 3) {
      cls = "border-amber-300 bg-amber-50 text-amber-800";
      label = `${label} · D-${d}`;
    } else {
      cls = "border-emerald-300 bg-emerald-50 text-emerald-700";
      label = `${label} · D-${d}`;
    }
  }
  const low = t.due_confidence === "low";
  return (
    <span className={CHIP + " " + cls} title={t.due_text ? `원문: ${t.due_text}` : ""}>
      {label}
      {low && " ?"}
    </span>
  );
}

function OriginChip({ t }: { t: MailTask }) {
  return t.origin === "internal" ? (
    <span className={CHIP + " border-sky-200 bg-sky-50 text-sky-700"}>사내</span>
  ) : (
    <span className={CHIP + " border-violet-200 bg-violet-50 text-violet-700"}>고객</span>
  );
}

/** CSS 막대 — 건수 비교용. 클릭하면 목록 필터가 걸린다. */
function Bars({
  items,
  onPick,
  active,
  empty,
}: {
  items: NameCount[];
  onPick?: (name: string) => void;
  active?: string;
  empty: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <ul className="space-y-1.5">
      {items.slice(0, 10).map((i) => (
        <li key={i.name}>
          <button
            onClick={() => onPick?.(i.name)}
            className={
              "flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-xs hover:bg-muted " +
              (active === i.name ? "bg-muted font-semibold" : "")
            }
            title={onPick ? "클릭하면 목록을 이 항목으로 필터합니다" : ""}
          >
            <span className="w-24 shrink-0 truncate sm:w-32" title={i.name}>
              {i.name}
            </span>
            <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-[#EB4E26]/80"
                style={{ width: `${Math.max(4, Math.round((i.count / max) * 100))}%` }}
              />
            </span>
            <span className="w-8 shrink-0 text-right tabular-nums">{i.count}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Kpi({ label, value, tone = "default" }: { label: string; value: number; tone?: "default" | "bad" | "warn" | "muted" }) {
  const cls =
    tone === "bad"
      ? "border-red-200 bg-red-50 text-red-700"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : tone === "muted"
          ? "border-border bg-muted text-muted-foreground"
          : "border-border bg-card";
  return (
    <div className={"rounded-xl border p-3 " + cls}>
      <div className="text-[11px] opacity-80">{label}</div>
      <div className="mt-0.5 text-xl font-bold tabular-nums">{value.toLocaleString("ko-KR")}</div>
    </div>
  );
}

/** 업무 1행 — 완료 / 미루기 / 기한·유형·고객사 수정 / 원문 링크. */
function TaskRow({
  t,
  today,
  types,
  onChange,
}: {
  t: MailTask;
  today: string;
  types: string[];
  onChange: (updated: MailTask) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(t.task_title);
  const [due, setDue] = useState(t.due_date);
  const [rtype, setRtype] = useState(t.request_type);
  const [customer, setCustomer] = useState(t.customer);

  useEffect(() => {
    setTitle(t.task_title);
    setDue(t.due_date);
    setRtype(t.request_type);
    setCustomer(t.customer);
  }, [t]);

  const patch = async (body: Parameters<typeof api.taskPatch>[1], msg?: string) => {
    setBusy(true);
    try {
      const r = await api.taskPatch(t.id, body);
      onChange(r.task);
      if (msg) toast(msg, "success");
      setEdit(false);
    } catch (e) {
      toast((e as Error).message || "저장에 실패했습니다.", "error");
    } finally {
      setBusy(false);
    }
  };

  const isOpen = t.status === "open" || t.status === "snoozed";
  const who = t.requester_name || t.requester_email;

  return (
    <li className={"rounded-xl border border-border bg-card p-3 " + (t.needs_review ? "border-amber-300" : "")}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {edit ? (
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={INPUT + " w-full text-sm"} maxLength={80} />
          ) : (
            <div className="text-sm font-medium leading-snug">
              {t.task_title}
              {t.needs_review && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] font-normal text-amber-700">
                  <AlertTriangle className="h-3 w-3" /> 확인 필요
                </span>
              )}
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <DueChip t={t} />
            <OriginChip t={t} />
            <span className={CHIP + " border-border bg-muted text-muted-foreground"}>{t.request_type}</span>
            <span className={CHIP + " border-border text-foreground"} title="고객사">
              {t.customer}
            </span>
            {t.replied_at && (
              <span className={CHIP + " inline-flex items-center gap-0.5 border-emerald-200 bg-emerald-50 text-emerald-700"} title={`회신 ${fmtDateTime(t.replied_at)}`}>
                <Reply className="h-3 w-3" /> 회신함
              </span>
            )}
            {t.has_attachment && (
              <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground" title={`첨부 ${t.attach_count}개`}>
                <Paperclip className="h-3 w-3" /> {t.attach_count}
              </span>
            )}
          </div>
          <div className="mt-1 truncate text-[11px] text-muted-foreground" title={t.subject}>
            {who && <span className="font-medium text-foreground/80">{who}</span>}
            {who && " · "}
            {fmtDateTime(t.received_at)}
            {t.subject && <> · {t.subject}</>}
          </div>
        </div>
        {t.page_url && (
          <a
            href={t.page_url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted"
            title="지식저장소에서 원문 스레드 보기(본인 전용)"
            aria-label="원문 보기"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>

      {edit && (
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-muted-foreground">기한</span>
            <div className="flex gap-1">
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={INPUT + " flex-1"} />
              {due && (
                <button onClick={() => setDue("")} className={BTN_SM} title="기한 지우기">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-muted-foreground">요청 유형</span>
            <select value={rtype} onChange={(e) => setRtype(e.target.value)} className={INPUT + " w-full"}>
              {types.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-muted-foreground">고객사</span>
            <input value={customer} onChange={(e) => setCustomer(e.target.value)} className={INPUT + " w-full"} maxLength={60} />
          </label>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {isOpen && !edit && (
          <>
            <button onClick={() => patch({ status: "done" }, "완료 처리했습니다.")} disabled={busy} className={BTN_SM}>
              <Check className="h-3 w-3" /> 완료
            </button>
            <button
              onClick={() => patch({ status: "snoozed", snoozed_until: addDays(today, 1) }, "내일 다시 보입니다.")}
              disabled={busy}
              className={BTN_SM}
              title="내일까지 목록에서 숨깁니다"
            >
              <Clock className="h-3 w-3" /> 내일
            </button>
            <button
              onClick={() => patch({ status: "snoozed", snoozed_until: addDays(today, 3) }, "3일 뒤 다시 보입니다.")}
              disabled={busy}
              className={BTN_SM}
              title="3일 뒤까지 목록에서 숨깁니다"
            >
              <Clock className="h-3 w-3" /> 3일 뒤
            </button>
            <button onClick={() => setEdit(true)} disabled={busy} className={BTN_SM}>
              <Pencil className="h-3 w-3" /> 수정
            </button>
            <button
              onClick={() => patch({ status: "dismissed" }, "무시 처리했습니다.")}
              disabled={busy}
              className={BTN_SM + " ml-auto text-muted-foreground"}
              title="할 일이 아니면 무시 — 목록에서 빠집니다"
            >
              <X className="h-3 w-3" /> 할 일 아님
            </button>
          </>
        )}
        {edit && (
          <>
            <button
              onClick={() =>
                patch(
                  {
                    task_title: title.trim() || undefined,
                    due_date: due,
                    request_type: rtype,
                    customer: customer.trim() || undefined,
                  },
                  "수정했습니다.",
                )
              }
              disabled={busy}
              className={BTN_SM + " bg-[#EB4E26] text-white hover:bg-[#EB4E26]"}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} 저장
            </button>
            <button onClick={() => setEdit(false)} disabled={busy} className={BTN_SM}>
              취소
            </button>
          </>
        )}
        {!isOpen && (
          <button onClick={() => patch({ status: "open" }, "다시 열었습니다.")} disabled={busy} className={BTN_SM}>
            <Undo2 className="h-3 w-3" /> 다시 열기
          </button>
        )}
        {t.status === "snoozed" && t.snoozed_until > today && (
          <span className="text-[11px] text-muted-foreground">({fmtDate(t.snoozed_until)}까지 미룸)</span>
        )}
      </div>
    </li>
  );
}

export default function Tasks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [optedIn, setOptedIn] = useState(false);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [pref, setPref] = useState<TaskPref | null>(null);

  const [tab, setTab] = useState<StatusTab>("open");
  const [customer, setCustomer] = useState("");
  const [rtype, setRtype] = useState("");
  const [due, setDue] = useState<TaskListParams["due"]>("any");
  const [q, setQ] = useState("");
  const [items, setItems] = useState<MailTask[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);

  const today = summary?.today ?? new Date().toISOString().slice(0, 10);
  const types = summary?.request_types ?? [];
  const imminent = summary?.imminent ?? [];
  // 훅은 조기 return 앞에 있어야 한다(렌더마다 같은 순서)
  const imminentIds = useMemo(() => new Set(imminent.map((t) => t.id)), [imminent]);

  const loadSummary = useCallback(async () => {
    const s = await api.taskSummary();
    setSummary(s);
    setPref(s.pref);
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await api.taskList({ status: tab, customer, request_type: rtype, due, q, limit: 300 });
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      toast((e as Error).message || "목록을 불러오지 못했습니다.", "error");
    } finally {
      setListLoading(false);
    }
  }, [tab, customer, rtype, due, q]);

  // 최초: 기능·옵트인 확인 → 요약
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const st = await api.mailStatus();
        if (!alive) return;
        const on = !!st.enabled && !!st.task_feature;
        setEnabled(on);
        if (!on) return;
        const acc = await api.mailAccount();
        if (!alive) return;
        const opted = !!acc.account?.task_enabled;
        setOptedIn(opted);
        if (!opted) return;
        await loadSummary();
      } catch (e) {
        if (!alive) return;
        setEnabled(false);
        toast((e as Error).message || "업무 정보를 불러오지 못했습니다.", "error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadSummary]);

  useEffect(() => {
    if (enabled && optedIn) void loadList();
  }, [enabled, optedIn, loadList]);

  const refreshAll = async () => {
    try {
      await Promise.all([loadSummary(), loadList()]);
    } catch (e) {
      toast((e as Error).message || "새로고침에 실패했습니다.", "error");
    }
  };

  /** 행 수정 후 — 목록은 즉시 갱신, KPI 는 다시 계산. */
  const onRowChange = (u: MailTask) => {
    setItems((prev) => {
      const stillVisible =
        tab === "open" ? (u.status === "open" || (u.status === "snoozed" && (!u.snoozed_until || u.snoozed_until <= today))) : u.status === tab;
      return stillVisible ? prev.map((x) => (x.id === u.id ? u : x)) : prev.filter((x) => x.id !== u.id);
    });
    void loadSummary();
  };

  const savePref = async (p: Partial<TaskPref>) => {
    try {
      const r = await api.taskPrefsSave(p);
      setPref(r.pref);
      toast("알림 설정을 저장했습니다.", "success");
    } catch (e) {
      toast((e as Error).message || "저장에 실패했습니다.", "error");
    }
  };

  const header = (
    <header className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background/90 px-2 py-2 backdrop-blur" style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}>
      <button
        onClick={() => navigate("/")}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-muted"
        aria-label="채팅으로 돌아가기"
        title="채팅으로 돌아가기"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <div className="flex items-center gap-1.5 font-bold">
        <ListChecks className="h-5 w-5 text-[#EB4E26]" />
        내 업무
      </div>
      <span className="ml-2 hidden text-xs text-muted-foreground sm:inline">내 메일에서 추출한 할 일·기한 (본인만 봅니다)</span>
      {enabled && optedIn && (
        <button onClick={refreshAll} className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted" aria-label="새로고침" title="새로고침">
          <RefreshCw className={"h-4 w-4 " + (listLoading ? "animate-spin" : "")} />
        </button>
      )}
    </header>
  );

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> 불러오는 중…
      </div>
    );
  }

  if (enabled === false || !optedIn) {
    return (
      <div className="flex h-full flex-col">
        <SessionGuard />
        {header}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-3 py-6">
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <Info className="mx-auto h-7 w-7 text-muted-foreground" />
              {enabled === false ? (
                <>
                  <div className="mt-2 text-sm font-medium">관리자가 기능을 활성화하지 않았습니다</div>
                  <p className="mt-1 text-xs text-muted-foreground">메일 업무비서는 현재 사용할 수 없습니다.</p>
                </>
              ) : (
                <>
                  <div className="mt-2 text-sm font-medium">메일 업무비서가 켜져 있지 않습니다</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    「내 메일 연동」에서 메일을 연동하고 「메일 업무비서」를 켜면, 저장된 내 메일에서 할 일·기한을 자동으로 뽑아 여기에 보여 줍니다.
                  </p>
                  <button onClick={() => navigate("/mail")} className={BTN + " mt-3"}>
                    내 메일 연동으로 이동
                  </button>
                </>
              )}
            </div>
          </div>
        </main>
        <Toasts />
      </div>
    );
  }

  const kpi = summary?.kpi;

  return (
    <div className="flex h-full flex-col">
      <SessionGuard />
      {header}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-3 px-3 py-4">
          {/* KPI */}
          {kpi && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Kpi label="미처리" value={kpi.open} />
              <Kpi label="오늘 마감" value={kpi.due_today} tone={kpi.due_today ? "bad" : "default"} />
              <Kpi label="이번 주 마감" value={kpi.due_week} tone={kpi.due_week ? "warn" : "default"} />
              <Kpi label="기한 지남" value={kpi.overdue} tone={kpi.overdue ? "bad" : "muted"} />
            </div>
          )}
          <p className="px-1 text-[11px] text-muted-foreground">
            마지막 추출 {fmtDateTime(summary?.last_extract_at) }
            {summary?.pending_queue ? ` · 추출 대기 ${summary.pending_queue}건` : ""}
            {kpi?.needs_review ? ` · 확인 필요 ${kpi.needs_review}건` : ""}
            {kpi?.no_due ? ` · 기한 없음 ${kpi.no_due}건` : ""}
          </p>

          {/* 기한 임박 */}
          <div className={CARD}>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold">기한 임박 · 지남</div>
              <span className="text-[11px] text-muted-foreground">{imminent.length}건</span>
            </div>
            {imminent.length === 0 ? (
              <p className="text-xs text-muted-foreground">3일 이내 마감이나 기한이 지난 항목이 없습니다.</p>
            ) : (
              <ul className="space-y-2">
                {imminent.map((t) => (
                  <TaskRow key={t.id} t={t} today={today} types={types} onChange={onRowChange} />
                ))}
              </ul>
            )}
          </div>

          {/* 건수 */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className={CARD}>
              <div className={LABEL}>고객별 미처리 건수</div>
              <Bars
                items={summary?.by_customer ?? []}
                active={customer}
                onPick={(n) => {
                  setCustomer((c) => (c === n ? "" : n));
                  setTab("open");
                }}
                empty="미처리 항목이 없습니다."
              />
            </div>
            <div className={CARD}>
              <div className={LABEL}>유형별 미처리 건수</div>
              <Bars
                items={summary?.by_type ?? []}
                active={rtype}
                onPick={(n) => {
                  setRtype((c) => (c === n ? "" : n));
                  setTab("open");
                }}
                empty="미처리 항목이 없습니다."
              />
            </div>
            <div className={CARD}>
              <div className={LABEL}>최근 30일 요청 유형 (수신 기준)</div>
              <Bars items={summary?.by_type_30d ?? []} empty="최근 30일 추출된 요청이 없습니다." />
              {summary && (
                <p className={HELP}>
                  고객 요청 {summary.by_origin_30d.customer}건 · 사내 요청 {summary.by_origin_30d.internal}건
                </p>
              )}
            </div>
            <div className={CARD}>
              <div className={LABEL}>최근 30일 고객별 요청</div>
              <Bars items={summary?.by_customer_30d ?? []} empty="최근 30일 추출된 요청이 없습니다." />
            </div>
            {/* 받은 메일 건수 (2026-09-30) — 위 카드들은 '요청(할 일)' 건수이고, 이 두 카드는 할 일이
                없는 메일(공유·참조)까지 포함한 '받은 메일 통 수'. 서버 기능 off 면 mail_stats 가 없어 숨긴다. */}
            {summary?.mail_stats && (
              <>
                <div className={CARD}>
                  <div className={LABEL}>
                    최근 30일 고객별 받은 메일 ({summary.mail_stats.total_30d}통)
                  </div>
                  <Bars items={summary.mail_stats.by_customer_30d} empty="최근 30일 집계된 메일이 없습니다." />
                </div>
                <div className={CARD}>
                  <div className={LABEL}>최근 30일 유형별 받은 메일</div>
                  <Bars items={summary.mail_stats.by_type_30d} empty="최근 30일 집계된 메일이 없습니다." />
                  <p className={HELP}>
                    할 일이 없는 메일은 「공유·참조」, 내 회신과 같은 날 수집돼 분류를 건너뛴 메일은
                    「미분류」로 셉니다.
                    {summary.mail_stats.since
                      ? ` ${summary.mail_stats.since} 수신분부터 집계했습니다.`
                      : " 집계는 이 기능을 켠 뒤 새로 받은 메일부터 쌓입니다."}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* 목록 */}
          <div className={CARD}>
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {(
                [
                  ["open", "현재 요청"],
                  ["done", "완료"],
                  ["dismissed", "무시"],
                ] as [StatusTab, string][]
              ).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={"rounded-lg px-2.5 py-1 text-xs " + (tab === k ? "bg-[#EB4E26] font-semibold text-white" : "border border-border hover:bg-muted")}
                >
                  {label}
                </button>
              ))}
              <span className="ml-auto text-[11px] text-muted-foreground">{total}건</span>
            </div>
            <div className="mb-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              <select value={customer} onChange={(e) => setCustomer(e.target.value)} className={INPUT}>
                <option value="">고객사 전체</option>
                {(summary?.by_customer ?? []).map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </option>
                ))}
                {customer && !(summary?.by_customer ?? []).some((c) => c.name === customer) && <option value={customer}>{customer}</option>}
              </select>
              <select value={rtype} onChange={(e) => setRtype(e.target.value)} className={INPUT}>
                <option value="">유형 전체</option>
                {types.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <select value={due} onChange={(e) => setDue(e.target.value as TaskListParams["due"])} className={INPUT}>
                <option value="any">기한 전체</option>
                <option value="imminent">임박·지남</option>
                <option value="has">기한 있음</option>
                <option value="none">기한 없음</option>
              </select>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="검색(업무명·제목·요청자)" className={INPUT} />
            </div>
            {listLoading ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 불러오는 중…
              </div>
            ) : items.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">
                {tab === "open" ? "표시할 요청이 없습니다. 메일이 수집·추출되면 여기에 나타납니다." : "항목이 없습니다."}
              </p>
            ) : (
              <ul className="space-y-2">
                {items
                  .filter((t) => !(tab === "open" && due === "any" && !customer && !rtype && !q && imminentIds.has(t.id)))
                  .map((t) => (
                    <TaskRow key={t.id} t={t} today={today} types={types} onChange={onRowChange} />
                  ))}
              </ul>
            )}
            {tab === "open" && due === "any" && !customer && !rtype && !q && imminentIds.size > 0 && (
              <p className={HELP}>기한 임박·지남 항목은 위 「기한 임박」 카드에만 표시했습니다.</p>
            )}
          </div>

          {/* 알림 설정 */}
          <div className={CARD}>
            <div className={LABEL}>NAVER WORKS 알림</div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                <input type="checkbox" checked={!!pref?.notify_enabled} onChange={(e) => savePref({ notify_enabled: e.target.checked })} className="h-4 w-4 accent-primary" />
                {pref?.notify_enabled ? <BellRing className="h-4 w-4 text-[#EB4E26]" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
                기한 임박 알림 받기
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                <input type="checkbox" checked={!!pref?.quiet_weekend} onChange={(e) => savePref({ quiet_weekend: e.target.checked })} className="h-4 w-4 accent-primary" />
                주말에는 보내지 않기
              </label>
            </div>
            <p className={HELP}>
              평일 아침 한 번, 오늘·내일·3일 후 마감과 기한이 지난 건을 묶어 NAVER WORKS 봇 메시지로 보냅니다. 확신이 낮은 기한(? 표시)은 당일에만 알립니다. 관리자 정책으로 알림이 꺼져 있으면 오지 않습니다.
            </p>
          </div>

          <p className="pb-6 text-center text-[11px] text-muted-foreground">
            AI 가 기한·내용을 잘못 읽을 수 있습니다. 최종 확인은 메일함에서, 잘못된 항목은 「수정」으로 고쳐 주세요. 이 화면은 본인만 봅니다.
          </p>
        </div>
      </main>
      <Toasts />
    </div>
  );
}
